import { apiClient } from './client';
import type { ApiResponse } from '@/types/api';

/**
 * Driver API
 * Handles driver-related API calls
 */

export interface Driver {
  id: number;
  englishName: string;
  arabicName: string;
  isActive: boolean;
  totalInventory: number;
  inventoryBySupplier: {
    FK: number;
    M: number;
    VARTA: number;
    EMTRAC: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DriverInventoryItem {
  batteryId: number;
  size: string;
  brand: string;
  tradeName?: string;
  supplierId: number;
  supplierCode: string;
  quantity: number;
  salePrice: number;
}

export interface DriverDashboard {
  driver: Driver;
  totalInventory: number;
  inventoryBySupplier: {
    FK: number;
    M: number;
    VARTA: number;
    EMTRAC: number;
  };
  batteryInventory: Array<{
    batteryId: number;
    size: string;
    brand: string;
    tradeName?: string;
    salePrice: number;
    suppliers: {
      FK?: number;
      M?: number;
      VARTA?: number;
      EMTRAC?: number;
    };
  }>;
}

export interface UpdateInventoryRequest {
  batteryId: number;
  supplierId: number;
  action: 'sold' | 'return';
  quantity: number;
}

/**
 * Get all drivers
 */
export const getAllDrivers = async (): Promise<ApiResponse<{ drivers: Driver[] }>> => {
  const response = await apiClient.get('/drivers');
  return response.data;
};

/**
 * Get driver inventory by ID
 */
export const getDriverInventory = async (driverId: number): Promise<ApiResponse<{
  driver: Driver;
  inventory: DriverInventoryItem[];
  totalQuantity: number;
}>> => {
  const response = await apiClient.get(`/drivers/${driverId}/inventory`);
  return response.data;
};

/**
 * Get driver dashboard (for logged-in driver)
 */
export const getDriverDashboard = async (): Promise<ApiResponse<DriverDashboard>> => {
  const response = await apiClient.get('/drivers/dashboard');
  return response.data;
};

/**
 * Update driver inventory (sold or return)
 */
export const updateDriverInventory = async (
  data: UpdateInventoryRequest
): Promise<ApiResponse<{ message: string; action: string; quantity: number }>> => {
  const response = await apiClient.post('/drivers/inventory/update', data);
  return response.data;
};

/**
 * Export driver inventory to Excel
 */
export const exportDriverInventory = async (driverId: number): Promise<Blob> => {
  const response = await apiClient.get(`/drivers/${driverId}/export`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Download driver inventory Excel file
 */
export const downloadDriverInventory = async (driverId: number, driverName: string): Promise<void> => {
  const blob = await exportDriverInventory(driverId);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `driver-${driverName}-${Date.now()}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
